import type { JSX } from 'react';
import { useIntl } from 'react-intl';

import { PartnerData } from '../shared/types';
import {
	CalmLogo,
	CalmLogoImageContainer,
	CalmLogoImageRevampContainer,
	Container,
	ContentWrapper,
	HeroText,
	HeroWrapper,
	ImageContainer,
	LogoDivider,
	LogoWrapper,
	PartnerLogo,
	PartnerName,
	RevampLogoWrapper,
	SubHeroText,
} from './styles';

interface Props {
	activeScreen?: string;
	logoUrl?: string;
	partnerId: string;
	pageInfo: PartnerData['pageInfo'];
	partnerName?: string;
	partnerSlug?: string;
	isRevampRedempFlow?: boolean;
}

function Hero({
	activeScreen,
	logoUrl,
	partnerId,
	pageInfo,
	partnerName,
	partnerSlug,
	isRevampRedempFlow,
}: Props): JSX.Element {
	const showMessage = activeScreen === 'welcome' || activeScreen === 'error';
	const { formatMessage } = useIntl();
	const logoBranding = logoUrl && logoUrl.length > 0 ? logoUrl : pageInfo?.logo;
	const isErrorScreen = activeScreen === 'error';

	const showLogoBranding = logoBranding || !!pageInfo?.logo;

	function getPartnerBranding(isRevampRedempFlow?: boolean): JSX.Element | null {
		if (partnerId === '' || isErrorScreen) {
			return null;
		}
		if (logoBranding && showLogoBranding) {
			return (
				<>
					<LogoDivider isRevampRedempFlow={isRevampRedempFlow} />
					<ImageContainer isRevampRedempFlow={isRevampRedempFlow}>
						<PartnerLogo
							data-testid="partner_logo"
							alt={`The logo for ${partnerName}`}
							src={logoBranding}
							fill
							style={{ objectFit: 'contain', objectPosition: 'left' }}
						/>
					</ImageContainer>
				</>
			);
		}
		if (partnerName) {
			return (
				<>
					<LogoDivider isRevampRedempFlow={isRevampRedempFlow} />
					<PartnerName data-testid="partner_name" isRevampRedempFlow={isRevampRedempFlow}>
						{partnerName}
					</PartnerName>
				</>
			);
		}
		return null;
	}

	const isBrandingMissing = getPartnerBranding(isRevampRedempFlow) === null;
	const isOnlyCalmLogo = isErrorScreen || isBrandingMissing;

	if (isRevampRedempFlow) {
		return (
			<RevampLogoWrapper>
				<CalmLogoImageRevampContainer isOnlyCalmLogo={isOnlyCalmLogo} isEws={partnerSlug === 'ews'}>
					<CalmLogo
						alt={'Calm logo'}
						src="/_n/images/calm-logo-square.png"
						fill
						style={{ objectFit: 'contain', objectPosition: 'right' }}
					/>
				</CalmLogoImageRevampContainer>
				{partnerSlug !== 'ews' && getPartnerBranding(isRevampRedempFlow)}
			</RevampLogoWrapper>
		);
	}

	return (
		<Container>
			<HeroWrapper compact={!showMessage} disableGradient={pageInfo.disableGradient}>
				<ContentWrapper>
					<>
						{pageInfo.messages?.headline.id ? (
							<HeroText>
								{formatMessage(pageInfo.messages?.headline, {
									breakingLine: <p />,
								})}
							</HeroText>
						) : null}
						<LogoWrapper>
							<CalmLogoImageContainer>
								<CalmLogo
									alt={'Calm logo'}
									src="/_n/images/calm-logo-square.png"
									fill
									style={{ objectFit: 'contain', objectPosition: 'left' }}
								/>
							</CalmLogoImageContainer>
							{getPartnerBranding()}
						</LogoWrapper>
						{pageInfo.messages?.subheadline && (
							<SubHeroText>{formatMessage(pageInfo.messages?.subheadline)}</SubHeroText>
						)}
					</>
				</ContentWrapper>
			</HeroWrapper>
		</Container>
	);
}

export default Hero;
